import {
  Button,
  createStyles,
  Grid,
  Paper,
  TextField,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import React from "react";
import type { Corral, Retreat } from "shared/types";
import type { CorralFormState } from "../corral.types";
import { useCorralForm } from "../hooks/useCorralForm";
import { useCorralFormOptions } from "../hooks/useCorralFormOptions";

type CorralFormProps = {
  corrals: Corral[];
  onCancel: () => void;
  onSubmit: (formState: CorralFormState) => void;
  retreats: Retreat[];
  selectedCorral: Corral | null;
} & WithStyles<typeof styles>;

function CorralForm({
  classes,
  corrals,
  onCancel,
  onSubmit,
  retreats,
  selectedCorral,
}: CorralFormProps) {
  const { t } = useTranslations();

  const {
    canSubmitForm,
    formState,
    handleChangeName,
    handleChangeShortName,
    handleResetForm,
    handleSelectRetreat,
    handleSubmitForm,
  } = useCorralForm({ corrals, onSubmit, selectedCorral });

  const { retreatOptions } = useCorralFormOptions({ retreats });

  const handleCancelClick = () => {
    handleResetForm();
    onCancel();
  };

  const isEditing = selectedCorral != null;
  const headerText = isEditing
    ? t("locales:edit-corral")
    : t("locales:new-corral");

  return (
    <Paper className={classes.paper} square={true}>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.formHeader} gutterBottom={true}>
          {headerText}
        </Typography>
      </Grid>
      <Grid item sm={12} xs={12}>
        <Typography className={classes.subHeader} gutterBottom={true}>
          {t("common:basic-data")}
        </Typography>
      </Grid>
      <Grid item sm={12} xs={12} className={classes.fieldsWrapper}>
        <SelectDropdown
          className={classes.retreatInput}
          data-testid="retreat-select"
          error={formState.retreatId.error != null}
          helperText={formState.retreatId.error}
          label={{ text: t("locales:retreat"), required: true }}
          onChange={handleSelectRetreat}
          options={retreatOptions}
          value={formState.retreatId.value}
        />

        <TextField
          className={classes.defaultInput}
          data-testid="corral-name-input"
          error={formState.name.error != null}
          helperText={formState.name.error}
          InputLabelProps={{ className: classes.inputLabel }}
          label={t("locales:corral-name")}
          onChange={handleChangeName}
          required={true}
          value={formState.name.value}
        />

        <TextField
          className={classes.defaultInput}
          data-testid="corral-short-name-input"
          error={formState.shortName.error != null}
          helperText={formState.shortName.error}
          InputLabelProps={{ className: classes.inputLabel }}
          label={t("common:fields.short-name")}
          onChange={handleChangeShortName}
          required={true}
          value={formState.shortName.value}
        />
      </Grid>
      <Grid item sm={12} xs={12} className={classes.formActionsContainer}>
        <FormActions
          canSubmit={canSubmitForm}
          isEditing={isEditing}
          onCancel={handleCancelClick}
          onSubmit={handleSubmitForm}
        />
      </Grid>
    </Paper>
  );
}

type FormActionsProps = {
  canSubmit: boolean;
  isEditing: boolean;
  onCancel: () => void;
  onSubmit: () => void;
};

function FormActions({
  canSubmit,
  isEditing,
  onCancel,
  onSubmit,
}: FormActionsProps) {
  const { t } = useTranslations();

  const submitButtonText = isEditing
    ? t("common:actions.save")
    : t("common:actions.add");

  return (
    <>
      <Button color="primary" data-testid="cancel-button" onClick={onCancel}>
        {t("common:actions.cancel")}
      </Button>
      <Button
        color="primary"
        data-testid="submit-button"
        disabled={!canSubmit}
        onClick={onSubmit}
        variant="contained"
      >
        {submitButtonText}
      </Button>
    </>
  );
}

const styles = () =>
  createStyles({
    paper: {
      borderRadius: "16px",
      marginTop: "10px",
      padding: "20px",
      width: "100%",
    },
    formHeader: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginTop: "16px",
    },
    subHeader: {
      fontFamily: "Roboto",
      fontSize: "14px",
      letterSpacing: "-0.03px",
      color: "#535353",
      fontWeight: "bold",
      marginTop: "16px",
      marginBottom: "12px",
    },
    fieldsWrapper: {
      display: "flex",
      flexDirection: "row",
      gap: "32px",
      flexWrap: "wrap",
    },
    retreatInput: {
      width: "200px !important",
    },
    inputLabel: {
      width: "max-content",
    },
    defaultInput: {
      width: 200,
      color: "#666666",
    },
    formActionsContainer: {
      marginTop: "36px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: "20px",
      width: "100%",
    },
  });

export default withStyles(styles)(CorralForm);
